import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { REGION_UNBOUND_ROLES, FA_ISI_ROLES, FA_CREATE_ROLES } from "../lib/roles";
import { FA_STATUS_LABEL } from "../lib/statusLabels";
import { formatIDR, formatDate } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { FAStatusBadge } from "../components/etams/StatusBadge";
import { Plus, ExternalLink } from "lucide-react";
import {
  useRealtimeTable,
  useVisiblePolling,
  REALTIME_DOWN_POLL_MS,
} from "../lib/useRealtimeTable";
import { toast } from "sonner";
import { apiErrorMessage } from "../lib/inventoryApi";
import { useDaftar } from "../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../components/etams/PalangDaftar";

//: Cermin `_URUT_SAH_FA` di backend/routers/fa.py.
const URUT_SAH = ["created_at", "deadline_date", "fa_number", "order_status", "total_amount_idr", "nama_team"];
//: K206 — cermin `hak_baca_fa.URUT_SAH_RINGKAS` (peran yang hanya menerima nomor FA, status, tenggat).
const URUT_SAH_RINGKAS = ["created_at", "deadline_date", "fa_number", "order_status"];

// Saringan bawaan "Belum selesai": FA yang sudah diserahkan atau dibatalkan tidak ikut
// (dikirim sebagai `status_kecuali`). Terlihat sebagai chip dan bisa dihapus.
const BELUM_SELESAI = "DISERAHKAN,CANCELLED";

const KOLOM_CSV = [
  { kunci: "fa_number", judul: "No. FA", ambil: (f) => f.fa_number || "" },
  { kunci: "nama_team", judul: "Nama Team", ambil: (f) => f.nama_team || "" },
  { kunci: "customer", judul: "Customer", ambil: (f) => f.customer?.name || "" },
  { kunci: "kota", judul: "Kota", ambil: (f) => f.customer?.city || "" },
  { kunci: "store", judul: "Cabang", ambil: (f) => f.store?.name || "" },
  { kunci: "order_status", judul: "Status", ambil: (f) => FA_STATUS_LABEL[f.order_status] || f.order_status },
  { kunci: "deadline_date", judul: "Deadline", ambil: (f) => formatDate(f.deadline_date) },
  { kunci: "total_amount_idr", judul: "Total", ambil: (f) => f.total_amount_idr ?? "" },
];
// K206 / VONIS-HAK-BACA-ISI-FA: peran RINGKAS (Admin Produksi, Admin Gudang, Desainer, SPV Desainer) hanya menerima
// nomor FA, status, dan tenggat dari backend — ekspornya pun hanya tiga kolom itu.
const KOLOM_CSV_RINGKAS = KOLOM_CSV.filter((k) => ["fa_number", "order_status", "deadline_date"].includes(k.kunci));

// Jeda polling daftar. 12 detik MENIRU DEADLINE_POLL_MS di Dashboard — layar
// ringkasan yang dipantau, bukan layar detail yang sedang dikerjakan (8s di
// FADetail) dan bukan layar setelan yang jarang berubah (25s di Users/Settings).
const FA_LIST_POLL_MS = 12000;

// Q68-01 Batch 3. Komponen lokal `StatusBadge` DIHAPUS dari sini — namanya
// bentrok persis dengan komponen kanonik `components/etams/StatusBadge.jsx`
// (GAP-F), dan isinya menyalin FADetail.jsx baris demi baris. Alasan lama
// "disalin persis dari FADetail supaya daftar dan detail tidak berbeda"
// sekarang dijamin oleh hal yang lebih kuat: keduanya memanggil komponen
// yang SAMA, bukan dua salinan yang kebetulan cocok.
//
// Yang TIDAK berubah: status FA tetap dibaca dari satu kolom `order_status`,
// tanpa logika turunan dari `outstanding_amount_idr`/`dp_confirmed_at`, dan
// kode DB tanpa label tetap tampil apa adanya (`FAStatusBadge` memakai pola
// fallback yang sama).

export default function FAList() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const role = profile?.role?.code;
  // K206: isi FA (customer, cabang, nilai, rincian) hanya Owner/KC/CS — peran lain melihat tiga kolom saja dan baris
  // tidak membuka rincian FA (rutenya memang tertutup bagi mereka).
  const isiLengkap = FA_ISI_ROLES.includes(role);
  const bolehBuat = FA_CREATE_ROLES.includes(role);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [stores, setStores] = useState([]);

  // Siapa yang butuh filter store, dan store mana yang boleh muncul.
  //
  // BUKAN `isOwner || isKepalaCabang`. Yang menentukan bukan dua role itu,
  // melainkan apakah cakupan baca seseorang mencakup LEBIH DARI SATU store:
  //   - CS disaring backend ke `store_id == profile.store_id`, jadi filter
  //     store nol guna baginya → disembunyikan.
  //   - REGION_UNBOUND_ROLES membaca lintas region → seluruh store masuk akal.
  //   - sisanya terikat satu region → daftarnya dipersempit ke store region
  //     sendiri, supaya tidak ada pilihan yang pasti nol hasil.
  //
  // CATATAN: `_fa_scope_filter` di backend/routers/fa.py hanya mengistimewakan
  // OWNER. Sejak K206 (VONIS-HAK-BACA-ISI-FA, 8 Okt 2026) ADMIN_PURCHASING dan
  // FINANCE tidak membuka layar ini sama sekali (backend 403, rute FA_LIST_ROLES).
  const isRegionUnbound = REGION_UNBOUND_ROLES.includes(role);
  const canFilterStore = role !== "CS";
  const storeOptions = isRegionUnbound
    ? stores
    : stores.filter((s) => s.region_id === profile?.region_id);

  // FA / Order — pola daftar seragam mode SERVER (28 Sep 2026; `docs/POLA_DAFTAR.md`).
  // Cari (nomor FA / nama team / nama atau telepon customer), urut, saringan status,
  // cabang, rentang deadline, dan "belum selesai" dikerjakan server; posisinya tersimpan
  // di alamat, jadi Kembali dari rincian FA memulihkan halaman + saringan yang sama.
  const namaStore = (id) => stores.find((s) => s.id === id)?.name || "cabang";
  const daftar = useDaftar(items, {
    server: { total, urutSah: isiLengkap ? URUT_SAH : URUT_SAH_RINGKAS },
    urutAwal: { kunci: "created_at", arah: "desc" },
    saringan: {
      aktif: { label: "Tampil", param: "status_kecuali", bawaan: BELUM_SELESAI, tampil: () => "Belum selesai" },
      status: { label: "Status", param: "order_status", tampil: (v) => FA_STATUS_LABEL[v] || v },
      store: { label: "Cabang", param: "store_id", tampil: namaStore },
      dari: { label: "Deadline dari", param: "deadline_from", tampil: (v) => formatDate(v) },
      sampai: { label: "Deadline sampai", param: "deadline_to", tampil: (v) => formatDate(v) },
    },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;

  const bangunParams = useCallback((ovr = {}) => {
    const p = { ...paramSaringan };
    // CS disaring backend ke cabangnya sendiri — saringan cabang dari alamat lama diabaikan.
    if (!canFilterStore) delete p.store_id;
    return { ...p, ...(cari && { q: cari }), limit, offset, sort, arah, ...ovr };
  }, [paramSaringan, cari, limit, offset, sort, arah, canFilterStore]);

  const ambilHalaman = useCallback(async (off, n) => {
    const r = await api.get("/fa-orders", { params: bangunParams({ offset: off, limit: n }) });
    return r.data;
  }, [bangunParams]);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const res = await api.get("/fa-orders", { params: bangunParams() });
      setItems(res.data?.items || []);
      // `total` = grand total dari backend (count="exact"), BUKAN panjang
      // halaman ini. Menghitungnya sendiri dari `items` akan salah begitu
      // hasilnya lebih dari satu halaman.
      setTotal(typeof res.data?.total === "number" ? res.data.total : 0);
    } catch (e) {
      setErr(apiErrorMessage(e, "Daftar FA gagal dimuat."));
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [bangunParams]);

  useEffect(() => {
    api.get("/stores").then((r) => setStores(r.data || [])).catch((e) => toast.error(apiErrorMessage(e, "Data pendukung gagal dimuat.")));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Realtime jalur utama, polling jaring pengaman (D108) — konvensi J3-RT02.
  // Bukan supabase.channel() mentah: hook ini yang memegang penyambungan ulang
  // bertahap dan memberi tahu layar saat channel-nya putus.
  // ⛔ K206: kanal realtime `fa_orders` membawa BARIS UTUH (catatan, nilai, customer) ke peramban pelanggan —
  //    hanya untuk peran yang berhak atas isi FA. Peran RINGKAS cukup polling (bentuk ringkas dari backend).
  const rt = useRealtimeTable("fa_orders", load, { enabled: isiLengkap });
  useVisiblePolling(load, rt ? FA_LIST_POLL_MS : REALTIME_DOWN_POLL_MS);

  const adaSaringan = daftar.cari.trim() || daftar.saringanAktif.length;

  return (
    <div className="space-y-6" data-testid="fa-list-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">FA / Order</h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="fa-list-keterangan">
            {isiLengkap
              ? "Daftar Final Agreement beserta status dan deadline produksinya."
              : "Nomor FA, status, dan deadline. Isi pesanan hanya bisa dibuka Owner, Kepala Cabang, dan CS; rincian untuk produksi ada di Work Order."}
          </p>
        </div>
        {bolehBuat && (
          <Button data-testid="fa-new-btn" onClick={() => navigate("/fa-orders/new")}>
            <Plus size={16} className="mr-1" /> Buat FA Baru
          </Button>
        )}
      </div>

      <Card className="p-4">
        <PalangDaftar daftar={daftar} placeholder={isiLengkap ? "Cari nomor FA / nama team / customer" : "Cari nomor FA"}
                      testId="fa-palang"
                      ekspor={{ nama: "daftar_fa", kolom: isiLengkap ? KOLOM_CSV : KOLOM_CSV_RINGKAS, ambilHalaman }}>
          <select
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={daftar.nilaiSaringan.aktif}
            onChange={(e) => daftar.setSaringan("aktif", e.target.value)}
            aria-label="Saring FA selesai"
            data-testid="fa-filter-aktif"
          >
            <option value={BELUM_SELESAI}>Belum selesai</option>
            <option value="">Semua FA</option>
          </select>
          <select
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={daftar.nilaiSaringan.status}
            // Status tertentu dipilih → "Belum selesai" dilepas supaya dua saringan tidak saling mengosongkan
            // (mis. status Diserahkan + Belum selesai = selalu kosong).
            onChange={(e) => (e.target.value
              ? daftar.setBeberapaSaringan({ status: e.target.value, aktif: "" })
              : daftar.setSaringan("status", ""))}
            aria-label="Saring status"
            data-testid="fa-status-filter"
          >
            <option value="">Semua status</option>
            {Object.entries(FA_STATUS_LABEL).map(([code, label]) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
          {canFilterStore && (
            <select
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
              value={daftar.nilaiSaringan.store}
              onChange={(e) => daftar.setSaringan("store", e.target.value)}
              aria-label="Saring cabang"
              data-testid="fa-store-filter"
            >
              <option value="">Semua cabang</option>
              {storeOptions.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
            </select>
          )}
          <label className="flex items-center gap-1 text-xs text-muted-foreground">
            Deadline
            <Input
              data-testid="fa-deadline-from"
              type="date"
              className="h-9 w-auto"
              value={daftar.nilaiSaringan.dari}
              onChange={(e) => daftar.setSaringan("dari", e.target.value)}
              aria-label="Deadline dari"
            />
            s/d
            <Input
              data-testid="fa-deadline-to"
              type="date"
              className="h-9 w-auto"
              value={daftar.nilaiSaringan.sampai}
              onChange={(e) => daftar.setSaringan("sampai", e.target.value)}
              aria-label="Deadline sampai"
            />
          </label>
        </PalangDaftar>

        {err && (
          <div className="mb-3 p-3 rounded border border-danger-soft-border bg-danger-soft text-danger-soft-foreground text-sm" data-testid="fa-list-error">
            {err}
          </div>
        )}

        <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                  <tr className="text-left">
                    <ThUrut daftar={daftar} kunci="fa_number" className="px-3 py-2">No. FA</ThUrut>
                    {isiLengkap && <ThUrut daftar={daftar} kunci="nama_team" className="px-3 py-2">Nama Team</ThUrut>}
                    {isiLengkap && <th className="px-3 py-2">Customer</th>}
                    {isiLengkap && <th className="px-3 py-2">Cabang</th>}
                    <ThUrut daftar={daftar} kunci="order_status" className="px-3 py-2">Status</ThUrut>
                    <ThUrut daftar={daftar} kunci="deadline_date" className="px-3 py-2">Deadline</ThUrut>
                    {isiLengkap && <ThUrut daftar={daftar} kunci="total_amount_idr" className="px-3 py-2 text-right" align="right">Total</ThUrut>}
                    {isiLengkap && <th className="px-3 py-2"></th>}
                  </tr>
                </thead>
                <tbody>
                  {loading && daftar.tampil.length === 0 && (
                    <tr><td colSpan={isiLengkap ? 8 : 3} className="px-3 py-8 text-center text-muted-foreground" data-testid="fa-list-loading">Memuat...</td></tr>
                  )}
                  {!loading && daftar.tampil.length === 0 && (
                    <tr><td colSpan={isiLengkap ? 8 : 3} className="px-3 py-8 text-center text-muted-foreground" data-testid="fa-list-kosong">
                      {adaSaringan ? "Tidak ada FA yang cocok dengan pencarian atau saringan ini." : "Belum ada FA."}
                    </td></tr>
                  )}
                  {daftar.tampil.map((fa) => (
                    <tr
                      key={fa.id}
                      data-testid={`fa-row-${fa.id}`}
                      className={isiLengkap ? "border-t border-border hover:bg-muted/50 cursor-pointer" : "border-t border-border"}
                      onClick={isiLengkap ? () => navigate(`/fa-orders/${fa.id}`) : undefined}
                    >
                      {/* Nol kelas warna — SENGAJA. Sel ini mewarisi
                          `text-card-foreground` dari <Card>, jadi ia ikut
                          bertukar saat tema berganti. Kelas abu tua literal yang
                          lama adalah warna KERAS: benar di mode terang, dan
                          rgb(15,23,42) di atas kartu gelap saat mode gelap —
                          praktis tak terbaca. Kolom Total di baris bawah
                          memakai pola yang sama dan itulah satu-satunya sel
                          di tabel ini yang sudah benar di kedua tema. */}
                      <td className="px-3 py-2 font-medium">{fa.fa_number || "—"}</td>
                      {isiLengkap && <td className="px-3 py-2">{fa.nama_team || <span className="text-muted-foreground">—</span>}</td>}
                      {/* Kota ikut DI BAWAH nama customer, bukan kolom sendiri:
                          satu kolom lagi memaksa tabel ini menggulir mendatar
                          di layar 13 inci, dan kota nol pernah dicari sendirian
                          — ia selalu dibaca bersama nama pemesannya. */}
                      {isiLengkap && (
                        <td className="px-3 py-2 text-muted-foreground">
                          {fa.customer?.name || "—"}
                          {fa.customer?.city ? (
                            <span className="block text-xs">{fa.customer.city}</span>
                          ) : null}
                        </td>
                      )}
                      {isiLengkap && <td className="px-3 py-2 text-muted-foreground">{fa.store?.name || "—"}</td>}
                      <td className="px-3 py-2">
                        <FAStatusBadge value={fa.order_status} />
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{formatDate(fa.deadline_date)}</td>
                      {isiLengkap && <td className="px-3 py-2 text-right font-medium">{formatIDR(fa.total_amount_idr)}</td>}
                      {isiLengkap && (
                        <td className="px-3 py-2 text-right">
                          <ExternalLink size={14} className="inline text-muted-foreground" />
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

        <Paginasi daftar={daftar} testId="fa-paginasi" />
      </Card>
    </div>
  );
}
